import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import useSEO from '../hooks/useSEO';
import GameCard from '../components/GameCard';

function CreatorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCreator = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${API_URL}/creators/${id}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error('Server error');
      }
      const data = await response.json();
      setCreator(data);
      setError(null);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'TypeError' || err.message.includes('fetch') || err.message.includes('Failed to fetch')) {
        setError('server_unavailable');
      } else {
        setError('server_error');
      }
      console.error('Error fetching creator:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCreator();
  }, [id, fetchCreator]);

  useSEO({
    title: creator ? `${creator.name} | Nightfall` : 'Creator Details | Nightfall',
    description: creator ? `Discover games by ${creator.name} on Nightfall.` : 'View creator details on Nightfall',
    url: creator ? `https://nightfallshop.com/creator/${id}` : null
  });

  const handleBack = () => {
    navigate('/creators');
  };

  if (loading) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col">
        <div className="font-sans text-base lg:text-lg text-white text-center py-10">Loading creator...</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-12 max-w-md text-center mx-2">
          <svg className="w-16 h-16 mx-auto mb-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="font-semibold text-2xl text-white mb-3">
            {error === 'server_unavailable' ? 'Server Unavailable' : 'Server Error'}
          </h2>
          <p className="font-normal text-base text-white/60 mb-6">
            {error === 'server_unavailable' 
              ? "Sorry, we're having trouble connecting to the server. Please make sure the backend is running."
              : "Sorry, something went wrong on our end. Please try again later."}
          </p>
          <button
            onClick={fetchCreator}
            className="px-6 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200"
          >
            Try Again
          </button>
        </div>
      </main>
    );
  }

  if (!creator) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-12 max-w-md text-center mx-2">
          <h2 className="font-semibold text-2xl text-white mb-3">Creator Not Found</h2>
          <p className="font-normal text-base text-white/60 mb-6">
            The creator you're looking for doesn't exist.
          </p>
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200"
          >
            Back to Creators
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="p-2 lg:p-5 pt-0 flex flex-col w-full overflow-x-hidden">
      <style>{`
        .creator-align {
          margin-top: calc(5.75rem + 0.5rem - 6rem);
        }
        @media (min-width: 768px) {
          .creator-align {
            margin-top: calc(5.75rem + 1.25rem - 8rem);
          }
        }
      `}</style>

      <div className="w-full">
        {/* Creator Header Card */}
        <div className="creator-align mb-8 lg:mb-12">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={handleBack}
              className="text-white/60 hover:text-white transition-colors duration-200 text-sm font-medium"
            >
              Creators
            </button>
            <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white/60 text-sm font-medium">{creator.name}</span>
          </div>

          {/* Full Width Card */}
          <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl overflow-hidden shadow-xl w-full">
            <div className="flex flex-col md:flex-row gap-6 lg:gap-8 p-6 lg:p-8">
              {/* Profile Picture - Left */}
              <div className="w-full md:w-64 lg:w-80 md:flex-shrink-0">
                <div className="w-full aspect-square md:w-64 md:h-64 lg:w-80 lg:h-80 rounded-2xl overflow-hidden border border-dark-border bg-gradient-to-br from-dark-border via-dark-card to-dark-surface">
                  {creator.pfp ? (
                    <img
                      src={creator.pfp}
                      alt={creator.name}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="w-full h-full bg-gradient-to-br from-dark-card/80 to-dark-surface/60 flex items-center justify-center" style={{ display: creator.pfp ? 'none' : 'flex' }}>
                    <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-accent-blue/20 border-2 border-accent-blue/40 flex items-center justify-center">
                      <span className="text-4xl lg:text-5xl font-bold text-accent-blue/60">{creator.name.charAt(0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Creator Info - Right */}
              <div className="flex-1 flex flex-col justify-start gap-6">
                {/* Name at top */}
                <div>
                  <h1 className="font-semibold text-3xl lg:text-4xl text-white mb-2">{creator.name}</h1>
                  <p className="text-white/60 text-sm lg:text-base">Game Developer</p>
                </div>

                {/* Links below */}
                {(creator.itchLink || creator.patreonLink || creator.subscribestarLink) && (
                  <div className="flex flex-wrap gap-3">
                    {creator.itchLink && (
                      <a
                        href={creator.itchLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-5 py-3 bg-dark-bg/50 border border-dark-border rounded-xl text-white/70 hover:text-white hover:bg-dark-bg/70 hover:border-accent-blue/40 transition-all duration-200 group/link"
                      >
                        <div className="w-8 h-8 rounded-lg bg-accent-blue/10 border border-accent-blue/20 flex items-center justify-center group-hover/link:bg-accent-blue/20 group-hover/link:border-accent-blue/30 transition-colors">
                          <img src="/images/itch_logo.png" alt="Itch.io" className="w-5 h-5 object-contain" draggable={false} onDragStart={(e) => e.preventDefault()} />
                        </div>
                        <span className="text-sm font-medium">Itch.io</span>
                        <svg className="w-4 h-4 opacity-50 group-hover/link:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                    {creator.patreonLink ? (
                      <a
                        href={creator.patreonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-5 py-3 bg-dark-bg/50 border border-dark-border rounded-xl text-white/70 hover:text-white hover:bg-dark-bg/70 hover:border-accent-blue/40 transition-all duration-200 group/link"
                      >
                        <div className="w-8 h-8 rounded-lg bg-accent-blue/10 border border-accent-blue/20 flex items-center justify-center group-hover/link:bg-accent-blue/20 group-hover/link:border-accent-blue/30 transition-colors">
                          <img src="/images/patreon_logo.png" alt="Patreon" className="w-5 h-5 object-contain" draggable={false} onDragStart={(e) => e.preventDefault()} />
                        </div>
                        <span className="text-sm font-medium">Patreon</span>
                        <svg className="w-4 h-4 opacity-50 group-hover/link:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : creator.subscribestarLink ? (
                      <a
                        href={creator.subscribestarLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-5 py-3 bg-dark-bg/50 border border-dark-border rounded-xl text-white/70 hover:text-white hover:bg-dark-bg/70 hover:border-accent-blue/40 transition-all duration-200 group/link"
                      >
                        <div className="w-8 h-8 rounded-lg bg-accent-blue/10 border border-accent-blue/20 flex items-center justify-center group-hover/link:bg-accent-blue/20 group-hover/link:border-accent-blue/30 transition-colors">
                          <img src="/images/subscribestar_logo.png" alt="SubscribeStar" className="w-5 h-5 object-contain" draggable={false} onDragStart={(e) => e.preventDefault()} />
                        </div>
                        <span className="text-sm font-medium">SubscribeStar</span>
                        <svg className="w-4 h-4 opacity-50 group-hover/link:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Games Section */}
        <div className="w-full mb-8 lg:mb-12">
          <div className="mb-6 lg:mb-8">
            <h2 className="font-semibold text-3xl lg:text-4xl text-white">
              Games by {creator.name}
            </h2>
          </div>
          {creator.games && creator.games.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 w-full">
              {creator.games.map(game => (
                <GameCard key={game.id} game={game} hidePrice={true} />
              ))}
            </div>
          ) : (
            <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-12 lg:p-16 text-center">
              <p className="text-white/60 text-base lg:text-lg">No games available yet.</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default CreatorDetail;
